import type { Cue, Tree, TreeNode, TreeNodeObject, TreeNodeObjectRuby, TreeNodeObjectTagNameWithRt } from 'webvtt-parser';
import type { Logger } from '@mimir/common/types/logger';
import type { SubtitleCue, SubtitleCueObject } from '@mimir/common/types/subtitleCue';
import { frac } from '@mimir/common/util/integerMath';
import { WebVTTParser } from 'webvtt-parser';
import type { SubtitleTextCue } from '../closed-captions/cue/types';

export function parseVtt(logger: Logger, bytes: Uint8Array): SubtitleCue[] {
  const vttParser = new WebVTTParser();
  const vttData = vttParser.parse((new TextDecoder()).decode(bytes));

  return vttData.cues.map((cue) => convertCue(logger, cue));
}


function convertCue(logger: Logger, cue: Cue): SubtitleCue {
  const startTime = frac(Math.round(cue.startTime * 1000.0), 1000);
  const endTime = frac(Math.round(cue.endTime * 1000.0), 1000);

  return {
    startTime,
    endTime,
    root: convertTree(logger, cue.tree),
  };
}

function convertTree(logger: Logger, cue: Tree): SubtitleCueObject {
  const elements = cue.children.map((child) => convertTreeNode(logger, child));
  if (elements.length === 1) {
    return elements[0]!;
  } else {
    return {
      type: 'list',
      elements: cue.children.map((node) => convertTreeNode(logger, node)),
    };
  }
}

function convertTreeNode(logger: Logger, treeNode: TreeNode): SubtitleCueObject {
  const treeNodeType = treeNode.type;
  switch (treeNodeType) {
    case 'text':
      return { type: 'text', text: treeNode.value };
    case 'object': {
      return convertObject(logger, treeNode);
    }
    case 'timestamp': {
      logger.warn('Inline timestamps are unsupported');
      return { type: 'unsupported' };
    }
  }
}

function convertObject(logger: Logger, i: TreeNodeObject<TreeNodeObjectTagNameWithRt>): SubtitleCueObject {
  const name = i.name;
  switch (name) {
    case 'i': return { type: 'italics', inner:  convertTree(logger, i) };
    case 'b': return { type: 'bold', inner: convertTree(logger, i) };
    case 'u': return { type: 'underline', inner: convertTree(logger, i) };
    case 'v':
    case 'c':
    case 'lang':
    case 'rt':
      logger.warn(`Unsupported object type: ${i.name}`);
      return convertTree(logger, i);
    case 'ruby':
      return convertRuby(logger, i);
  }
}

function convertRuby(logger: Logger, i: TreeNodeObjectRuby): SubtitleCueObject {
  logger.warn(`Unsupported object type: ${i.name}`);
  return {
    type: 'list',
    elements: i.children.map((cue) => convertRubyChild(logger, cue)),
  };
}

function convertRubyChild(logger: Logger, treeNode: TreeNode<TreeNodeObjectTagNameWithRt>): SubtitleCueObject {
  const treeNodeType = treeNode.type;
  switch (treeNodeType) {
    case 'text':
      return { type: 'text', text: treeNode.value };
    case 'object': {
      return convertObject(logger, treeNode);
    }
    case 'timestamp': {
      logger.warn('Inline timestamps are unsupported');
      return { type: 'unsupported' };
    }
  }
}

/**
 * Flatten a SubtitleCueObject tree to plain text.
 */
function flattenCueObjectToText(obj: SubtitleCueObject): string {
  switch (obj.type) {
    case 'text':
      return obj.text;
    case 'list':
      return obj.elements.map(flattenCueObjectToText).join('');
    case 'bold':
    case 'underline':
    case 'italics':
      return flattenCueObjectToText(obj.inner);
    case 'unsupported':
      return '';
  }
}

export function parseVttString(logger: Logger, content: string): SubtitleTextCue[] {
  const vttParser = new WebVTTParser();
  const vttData = vttParser.parse(content);

  return vttData.cues.map((cue, index) => {
    const text = flattenCueObjectToText(convertTree(logger, cue.tree));

    return {
      startTime: cue.startTime,
      endTime: cue.endTime,
      text,
      cueIndex: index + 1,
    };
  });
}
